import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import Cookies from 'universal-cookie'
import toastr from 'toastr'
import { PostHogProvider } from 'posthog-js/react'
import axios from 'axios'

import ChecksubApp from '@Site/ChecksubApp'
import configureChecksubStore from '@Site/configureChecksubStore'

import 'toastr/build/toastr.css'

import './site.scss'

const rootEl = document.getElementById('root') as HTMLDivElement
const csrfTokenEl = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
const token = csrfTokenEl.content

const cookies = new Cookies()

toastr.options.closeButton = true

interface IChecksub {
  token: string
  readonly email?: string
  readonly s3Bucket: string
  readonly stripePublishableKey: string
  readonly essentialCostForMinute: number
  readonly cookies: Cookies
  readonly tooltipDelay: number
  readonly termbaseLanguageColors: string[]
  readonly nearBottomDistance: number
  readonly devMode: boolean
  readonly e2eMode: boolean
  readonly postHogHost: string
  readonly postHogKey: string
}

declare global {
  interface Window {
    Checksub: IChecksub
    gtag: typeof gtag
    dataLayer: any
    readonly Rollbar: any
  }
}

window.Checksub = {
  ...window.Checksub,
  token,
  cookies,
  tooltipDelay: 1000,
  termbaseLanguageColors: [
    '#43c78f', // green
    '#ca9ffc', // purple
    '#ffb558', // red
    '#72b7f9', // blue
    '#8159b4', // moderate violet
    '#e48c72', // soft red
    '#c15a87', // moderate pink
    '#bbc15a', // moderate yellow
    '#c1945a', // moderate orange
    '#a9a9a9', // gray
    '#ff92d2', // very light pink
    '#99cfe0', // very soft cyan
  ],
  nearBottomDistance: 300, // px
}

axios.interceptors.request.use(config => {
  const requestUrl = new URL(config.url || '', window.location.origin)

  if (requestUrl.origin === window.location.origin) {
    config.headers.set('X-CSRF-Token', window.Checksub.token)
  }

  return config
})

const store = configureChecksubStore()

const AppContent = () => (
  <Provider store={store}>
    <ChecksubApp />
  </Provider>
)

const root = createRoot(rootEl)

if (window.Checksub.devMode || window.Checksub.e2eMode) {
  root.render(
    <AppContent />
  )
} else {
  const postHogOptions = {
    api_host: window.Checksub.postHogHost,
    capture_exceptions: true,
    person_profiles: 'identified_only' as const,
  }

  root.render(
    <PostHogProvider
      apiKey={window.Checksub.postHogKey}
      options={postHogOptions}
    >
      <AppContent />
    </PostHogProvider>
  )
}
